import React, { useEffect, useState } from 'react';
import { request, signIn, signUp, signInSocial, authMode, pendingDevice } from '../api.js';
import { navigate, useMe } from '../router.jsx';

const SOCIAL = {
  google: { label: 'Continue with Google', icon: <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg> },
  github: { label: 'Continue with GitHub', icon: <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path fill="currentColor" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z"/></svg> },
};

// Social sign-in buttons enabled on the server (AUTH_PROVIDERS in /v1/config).
function useSocialProviders() {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (authMode !== 'neon') return;
    request('/v1/config', { anonymous: true }).then(c => setList((c.auth_providers || []).filter(p => SOCIAL[p])), () => setList([]));
  }, []);
  return list;
}

export function Login({ error: initialError, onSignedIn }) {
  // Coming from Forge Code (/device?code=…): new people usually need an account first.
  const fromCli = window.location.pathname === '/device';
  const [mode, setMode] = useState('in');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError);
  const social = useSocialProviders();
  const submit = async e => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (mode === 'up') await signUp(name || email.split('@')[0], email, password); else await signIn(email, password);
      await onSignedIn();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const socialSignIn = async provider => {
    setError(null);
    try { await signInSocial(provider); } catch (err) { setError(err.message); }
  };
  return (
    <div className="center">
      <form className="card auth-card" onSubmit={submit}>
        <div className="brand" style={{ padding: 0, marginBottom: 12 }}><img src="/icon.svg" alt="" width="28" />{fromCli ? 'Forge Code' : 'Forge Console'}</div>
        <h1>{mode === 'up' ? 'Create your account' : 'Sign in'}</h1>
        <p className="muted">{authMode === 'dev' ? 'Development sign-in: any email works, no password. Set VITE_NEON_AUTH_URL to use Neon Auth.'
          : fromCli ? 'Forge Code needs a Forge account. Sign in or create one, then authorize your terminal.' : 'Your Forge account, for Forge Code and this console.'}</p>
        {social.length > 0 && <>
          <div className="social">
            {social.map(p => <button key={p} type="button" className="btn social-btn" onClick={() => socialSignIn(p)}>{SOCIAL[p].icon}{SOCIAL[p].label}</button>)}
          </div>
          <div className="divider"><span>or with email</span></div>
        </>}
        {mode === 'up' && <><label htmlFor="name">Name</label><input id="name" value={name} onChange={e => setName(e.target.value)} autoComplete="name" /></>}
        <label htmlFor="email">Email</label>
        <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
        {authMode === 'neon' && <><label htmlFor="password">Password</label><input id="password" type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)} autoComplete={mode === 'up' ? 'new-password' : 'current-password'} /></>}
        {error && <div className="error">{error}</div>}
        <div className="row" style={{ marginTop: 16 }}>
          <button className="btn primary" disabled={busy}>{busy ? 'Working…' : mode === 'up' ? 'Create account' : 'Sign in'}</button>
        </div>
        {authMode === 'neon' && <p className="muted" style={{ marginTop: 16 }}>
          {mode === 'up' ? 'Already have an account? ' : 'New to Forge? '}
          <a href="#" onClick={e => { e.preventDefault(); setError(null); setMode(mode === 'up' ? 'in' : 'up'); }}>{mode === 'up' ? 'Sign in' : 'Create an account'}</a>
        </p>}
      </form>
    </div>
  );
}

// /device?code=XXXX-XXXX: approve a Forge Code sign-in started by /login in the terminal.
export function Device() {
  const me = useMe();
  const [code, setCode] = useState(new URLSearchParams(window.location.search).get('code') || pendingDevice.get() || '');
  const [info, setInfo] = useState(null);
  const [state, setState] = useState('enter');
  const [error, setError] = useState(null);
  const lookup = async c => {
    setError(null);
    try { setInfo(await request(`/v1/device/${encodeURIComponent(c.trim().toUpperCase())}`)); setState('confirm'); } catch (e) { pendingDevice.clear(); setError(e.message); }
  };
  useEffect(() => { if (/^[A-Z]{4}-[A-Z]{4}$/i.test(code)) lookup(code); }, []);
  const answer = async approve => {
    pendingDevice.clear();
    try { await request('/v1/device/approve', { method: 'POST', json: { user_code: info.user_code, approve } }); setState(approve ? 'approved' : 'denied'); } catch (e) { setError(e.message); }
  };
  return (
    <div className="center">
      <div className="card auth-card">
        <div className="brand" style={{ padding: 0, marginBottom: 12 }}><img src="/icon.svg" alt="" width="28" />Forge Code</div>
        <h1>{state === 'approved' ? 'You\'re signed in' : 'Authorize Forge Code'}</h1>
        {state === 'enter' && <>
          <p className="muted">Enter the code shown in your terminal.</p>
          <form onSubmit={e => { e.preventDefault(); lookup(code); }}>
            <input className="code" value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="XXXX-XXXX" maxLength={9} aria-label="Device code" />
            <div className="row" style={{ marginTop: 12 }}><button className="btn primary">Continue</button></div>
          </form>
        </>}
        {state === 'confirm' && info && <>
          <div className="code" style={{ margin: '12px 0' }}>{info.user_code}</div>
          <p><b>{info.client_name}</b>{info.hostname ? <> on <b>{info.hostname}</b></> : null} wants to sign in to your Forge account <b>{me.user.email}</b>.</p>
          <p className="muted">Check that this code matches the one in your terminal.</p>
          <p className="muted">It will be able to run models on your plan, share conversations and start cloud sessions. Only approve a code you just saw in your own terminal.</p>
          <div className="row"><button className="btn primary" onClick={() => answer(true)}>Authorize</button><button className="btn" onClick={() => answer(false)}>Cancel</button></div>
        </>}
        {state === 'approved' && <><p className="pill ok">Authorized</p><p>Forge Code is now signed in as <b>{me.user.email}</b>. Go back to your terminal: Forge Code has already continued. You can close this tab.</p><a href="/account" onClick={e => { e.preventDefault(); navigate('/account'); }}>Open the console</a></>}
        {state === 'denied' && <p>Cancelled. Forge Code was not signed in; the terminal will show that the request was refused.</p>}
        {error && <div className="error">{error}</div>}
      </div>
    </div>
  );
}
